<script setup lang="ts">
import { computed, unref } from 'vue';
import PlsText from './PlsText.vue';
import { useModalCustomisation } from '@/common/customisation/composables/useModalCustomisation';
import {
  createBackgroundStyle,
  createBorderStyle,
  createTextStyle,
  combineStyles,
} from '@/common/customisation/utils/styleUtils';
import PlsButton1 from './PlsButton1.vue';

export interface PlsDialogAction {
  id: string | number;
  text: string;
  color: 'primary' | 'secondary' | 'text';
  action?: () => void;
  testId?: string;
}

export interface PlsDialogProps {
  open: boolean;
  title?: string;
  content?: string | object;
  actionDirection?: 'row' | 'column';
  actions?: PlsDialogAction[];
  fullWidth?: boolean;
}

const props = defineProps<PlsDialogProps>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'submit'): void;
}>();

// Get customisation
const customisation = useModalCustomisation();

// Computed styles
const containerStyle = computed(() =>
  combineStyles(
    createBackgroundStyle(customisation.container.backgroundColor),
    createBorderStyle(customisation.container.borderColor)
  )
);

const titleStyle = computed(() => createTextStyle(customisation.header.text.color));

const contentStyle = computed(() => createTextStyle(customisation.content.textColor));

const getButtonCustomisation = (color: 'primary' | 'secondary' | 'text') => {
  if (color === 'text') {
    return {
      backgroundColor: undefined as string | undefined,
      textColor: unref(customisation.buttons.text.textColor),
      borderColor: undefined as string | undefined,
    };
  }
  const btnConfig = color === 'primary' ? customisation.buttons.primary : customisation.buttons.secondary;
  return {
    backgroundColor: unref(btnConfig.backgroundColor),
    textColor: unref(btnConfig.textColor),
    borderColor: unref(btnConfig.borderColor),
  };
};

const actions = computed<PlsDialogAction[]>(() => {
  if (props.actions) return props.actions;
  return [
    { id: 'close', text: 'Close', color: 'secondary', action: () => emit('close') },
    { id: 'submit', text: 'Submit', color: 'primary', action: () => emit('submit') },
  ];
});
</script>

<template>
  <transition name="fade">
    <div v-if="open" class="pls-dialog-overlay">
      <div
        class="pls-dialog-wrapper"
        :class="{ 'full-width': fullWidth, 'card-radius': !fullWidth }"
        :style="containerStyle"
      >
        <slot name="icon" />

        <div v-if="title || $slots.title" class="pls-dialog-header">
          <slot name="title">
            <PlsText size-variant="title" component="h3" :style="titleStyle">{{ title }}</PlsText>
          </slot>
        </div>

        <div v-if="content || $slots.default" class="pls-dialog-content" :style="contentStyle">
          <PlsText v-if="content" :style="contentStyle">{{ content }}</PlsText>
          <slot v-else />
        </div>

        <div v-if="actions && actions.length > 0" class="pls-dialog-actions">
          <div v-for="action in actions" :key="action.id" class="pls-dialog-btn-container">
            <PlsButton1
              :data-testid="action.testId"
              :background-color="getButtonCustomisation(action.color).backgroundColor"
              :text-color="getButtonCustomisation(action.color).textColor"
              :border-color="getButtonCustomisation(action.color).borderColor"
              @click="action.action"
            >
              {{ action.text }}
            </PlsButton1>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.pls-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.3);
  z-index: 50;
  backdrop-filter: blur(16px);
}

.pls-dialog-wrapper {
  position: relative;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  width: 100%;
  max-width: 85vw;
  padding: 3rem;

  text-align: center;
}

.pls-dialog-wrapper.full-width {
  max-width: 100vw;
  height: 100vh;
}

.pls-dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  justify-content: center;
}

.pls-dialog-content {
  width: 100%;
  margin-top: 1rem;
}

.pls-dialog-actions {
  display: flex;
  flex-direction: row;
  justify-content: center;
  margin-top: 1.5rem;
  gap: 12px;
  flex-wrap: wrap;
  width: 100%;
}

.pls-dialog-btn-container {
  flex: 1 1 calc(50% - 6px);
  min-width: 0;
  max-width: calc(50% - 6px);
}

/* When there are 3 buttons, make the last one take full width */
.pls-dialog-btn-container:nth-child(3):last-child {
  flex: 1 1 100%;
  max-width: 100%;
}
</style>
